/**
 * Console Capture Utility
 *
 * Shared console capturing logic used by both GlobalDevBar and SweetlinkBridge.
 * Provides consistent console interception and log formatting.
 */

import type { ConsoleLog } from '../types.js';

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of console logs to retain */
export const MAX_CONSOLE_LOGS = 100;

// ============================================================================
// Types
// ============================================================================

/**
 * Original console methods stored for restoration
 */
export interface OriginalConsoleMethods {
  log: typeof console.log;
  error: typeof console.error;
  warn: typeof console.warn;
  info: typeof console.info;
}

/**
 * Console capture state
 */
export interface ConsoleCaptureState {
  logs: ConsoleLog[];
  errorCount: number;
  warningCount: number;
  originalConsole: OriginalConsoleMethods | null;
  isPatched: boolean;
}

/**
 * Configuration for console capture
 */
export interface ConsoleCaptureConfig {
  maxLogs?: number;
  trackCounts?: boolean;
  onLog?: (log: ConsoleLog) => void;
}

/**
 * Listener callback for log count changes
 */
export type LogChangeListener = (
  errorCount: number,
  warningCount: number,
  infoCount: number
) => void;

// ============================================================================
// Formatting Utilities
// ============================================================================

/**
 * Format a single argument for logging
 */
export function formatArg(arg: unknown): string {
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  }
  if (typeof arg === 'string' || typeof arg === 'number' || typeof arg === 'boolean') {
    return String(arg);
  }
  if (typeof arg === 'object' && arg !== null) {
    try {
      return JSON.stringify(arg, (_key, val) => {
        if (val instanceof Error) {
          return `${val.name}: ${val.message}`;
        }
        return val;
      });
    } catch {
      return '[object]';
    }
  }
  return String(arg);
}

/**
 * Format multiple arguments into a single message string
 */
export function formatArgs(args: unknown[]): string {
  return args.map(formatArg).join(' ');
}

// ============================================================================
// Console Capture Class
// ============================================================================

/**
 * ConsoleCapture - Captures console output and stores logs
 */
export class ConsoleCapture {
  private logs: ConsoleLog[] = [];
  private errorCount = 0;
  private warningCount = 0;
  private infoCount = 0;
  private originalConsole: OriginalConsoleMethods | null = null;
  private isPatched = false;
  private maxLogs: number;
  private trackCounts: boolean;
  private onLog?: (log: ConsoleLog) => void;
  private listeners: LogChangeListener[] = [];

  constructor(config: ConsoleCaptureConfig = {}) {
    this.maxLogs = config.maxLogs ?? MAX_CONSOLE_LOGS;
    this.trackCounts = config.trackCounts ?? true;
    this.onLog = config.onLog;
  }

  /**
   * Trim logs to maxLogs and adjust counts for any evicted entries.
   */
  private trimLogs(): void {
    if (this.logs.length <= this.maxLogs) return;
    const evicted = this.logs.slice(0, this.logs.length - this.maxLogs);
    this.logs = this.logs.slice(-this.maxLogs);
    if (this.trackCounts) {
      for (const log of evicted) {
        if (log.level === 'error') this.errorCount--;
        else if (log.level === 'warn') this.warningCount--;
        else if (log.level === 'info') this.infoCount--;
      }
    }
  }

  /**
   * Start capturing console output
   */
  start(): void {
    if (typeof window === 'undefined') return;
    if (this.isPatched) return;

    this.originalConsole = {
      log: console.log.bind(console),
      error: console.error.bind(console),
      warn: console.warn.bind(console),
      info: console.info.bind(console),
    };

    const captureLog = (level: ConsoleLog['level'], args: unknown[]) => {
      const log: ConsoleLog = {
        level,
        message: formatArgs(args),
        timestamp: Date.now(),
      };

      this.logs.push(log);

      if (this.trackCounts) {
        if (level === 'error') this.errorCount++;
        else if (level === 'warn') this.warningCount++;
        else if (level === 'info') this.infoCount++;
      }

      this.trimLogs();

      if (this.onLog) {
        this.onLog(log);
      }

      this.notifyListeners();
    };

    // Patch all console methods with a unified wrapper
    const levels = ['log', 'error', 'warn', 'info'] as const;
    for (const level of levels) {
      const original = this.originalConsole![level];
      console[level] = (...args: unknown[]) => {
        captureLog(level, args);
        original(...args);
      };
    }

    this.isPatched = true;
  }

  /**
   * Stop capturing and restore original console methods
   */
  stop(): void {
    if (!this.isPatched || !this.originalConsole) return;

    console.log = this.originalConsole.log;
    console.error = this.originalConsole.error;
    console.warn = this.originalConsole.warn;
    console.info = this.originalConsole.info;

    this.isPatched = false;
  }

  /**
   * Get all captured logs
   */
  getLogs(): ConsoleLog[] {
    return [...this.logs];
  }

  /**
   * Get filtered logs
   */
  getFilteredLogs(filter: string): ConsoleLog[] {
    const filterLower = filter.toLowerCase();
    return this.logs.filter(
      (log) => log.level === filterLower || log.message.toLowerCase().includes(filterLower)
    );
  }

  /**
   * Get error count
   */
  getErrorCount(): number {
    return this.errorCount;
  }

  /**
   * Get warning count
   */
  getWarningCount(): number {
    return this.warningCount;
  }

  /**
   * Get info count
   */
  getInfoCount(): number {
    return this.infoCount;
  }

  /**
   * Add a listener for log count changes
   */
  addListener(listener: LogChangeListener): void {
    this.listeners.push(listener);
  }

  /**
   * Remove a listener
   */
  removeListener(listener: LogChangeListener): void {
    const idx = this.listeners.indexOf(listener);
    if (idx !== -1) this.listeners.splice(idx, 1);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.errorCount, this.warningCount, this.infoCount);
      } catch {
        // Ignore listener errors
      }
    }
  }

  /**
   * Get current state
   */
  getState(): ConsoleCaptureState {
    return {
      logs: this.getLogs(),
      errorCount: this.errorCount,
      warningCount: this.warningCount,
      originalConsole: this.originalConsole,
      isPatched: this.isPatched,
    };
  }

  /**
   * Recompute the cached counts from the current buffer. Use after any
   * batch operation (importLogs, clear-then-add) to guarantee counts can
   * never drift from the actual buffer contents.
   */
  private recomputeCounts(): void {
    if (!this.trackCounts) return;
    let e = 0;
    let w = 0;
    let i = 0;
    for (const log of this.logs) {
      if (log.level === 'error') e++;
      else if (log.level === 'warn') w++;
      else if (log.level === 'info') i++;
    }
    this.errorCount = e;
    this.warningCount = w;
    this.infoCount = i;
  }

  /**
   * Import logs from another source (e.g., early capture).
   * Counts are recomputed from the buffer afterward — a batch import is
   * not subject to the same incremental accounting as the per-message
   * capture path, so this is the safe form.
   */
  importLogs(logs: ConsoleLog[]): void {
    this.logs = [...logs, ...this.logs];
    this.trimLogs();
    this.recomputeCounts();
  }

  /**
   * Import early logs from window.__sweetlinkEarlyLogs (set by EARLY_CONSOLE_CAPTURE_SCRIPT)
   * and clear the global array. Converts ISO timestamp strings to epoch milliseconds.
   */
  importEarlyLogs(): void {
    if (typeof window === 'undefined' || !window.__sweetlinkEarlyLogs) return;

    const earlyLogs: ConsoleLog[] = window.__sweetlinkEarlyLogs.map((log) => ({
      level: log.level,
      message: log.message,
      timestamp:
        typeof log.timestamp === 'string'
          ? new Date(log.timestamp).getTime()
          : Number(log.timestamp),
    }));

    this.importLogs(earlyLogs);
    window.__sweetlinkEarlyLogs = [];
  }

  /**
   * Push a log entry directly (used by error/rejection handlers)
   */
  addLog(log: ConsoleLog): void {
    this.logs.push(log);
    if (this.trackCounts) {
      if (log.level === 'error') this.errorCount++;
      else if (log.level === 'warn') this.warningCount++;
      else if (log.level === 'info') this.infoCount++;
    }
    this.trimLogs();
    this.notifyListeners();
  }

  /**
   * Start listening for uncaught errors, unhandled rejections,
   * resource load failures, and CSP violations.
   */
  startErrorHandlers(): () => void {
    if (typeof window === 'undefined') return () => {};

    // --- Uncaught JS errors (ErrorEvent on window, bubble phase) ----------
    const errorHandler = (event: Event) => {
      if (!(event instanceof ErrorEvent)) return;

      this.addLog({
        level: 'error',
        message: `Uncaught: ${event.message}`,
        timestamp: Date.now(),
        source: event.filename,
        stack: event.error?.stack,
      });
    };

    // --- Unhandled promise rejections ------------------------------------
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      this.addLog({
        level: 'error',
        message:
          reason instanceof Error
            ? `Unhandled rejection: ${reason.name}: ${reason.message}`
            : `Unhandled rejection: ${String(reason)}`,
        timestamp: Date.now(),
        stack: reason?.stack,
      });
    };

    // --- Resource load errors (404, CORS, network failures) --------------
    // These fire a plain Event (not ErrorEvent) on the failing element in
    // capture phase. We listen in capture phase because they don't bubble.
    const resourceErrorHandler = (event: Event) => {
      // Only handle resource errors, not JS errors (those are ErrorEvent)
      if (event instanceof ErrorEvent) return;

      const target = event.target as HTMLElement & {
        src?: string;
        href?: string;
        tagName?: string;
      };
      if (!target?.tagName) return;

      // Skip devbar's own resources to avoid feedback loops, but only
      // when the URL plausibly belongs to one of the packages — a bare
      // substring match silently drops legit user assets like
      // /assets/devbar-logo.png. Match on the path, anchored to the
      // package directory, instead.
      const url = target.src || target.href || '';
      if (!url) return;
      try {
        const urlPath = new URL(url, window.location.href).pathname;
        if (
          /\/@ytspar\/(devbar|sweetlink)\//.test(urlPath) ||
          /\/node_modules\/@ytspar\/(devbar|sweetlink)\//.test(urlPath)
        ) {
          return;
        }
      } catch {
        /* malformed URL — let it through and log as a normal error */
      }

      const tagName = target.tagName.toLowerCase();
      // Only track meaningful resource types
      if (!['img', 'script', 'link', 'audio', 'video', 'source'].includes(tagName)) return;

      this.addLog({
        level: 'error',
        message: `Failed to load resource: ${url}`,
        timestamp: Date.now(),
        source: url,
      });
    };

    // --- CSP violations --------------------------------------------------
    const cspHandler = (event: SecurityPolicyViolationEvent) => {
      this.addLog({
        level: 'error',
        message: `CSP violation: '${event.violatedDirective}' blocked ${event.blockedURI || '(inline)'}`,
        timestamp: Date.now(),
        source: event.sourceFile || undefined,
      });
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', rejectionHandler);
    window.addEventListener('error', resourceErrorHandler, true); // capture phase
    window.addEventListener('securitypolicyviolation', cspHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', rejectionHandler);
      window.removeEventListener('error', resourceErrorHandler, true);
      window.removeEventListener('securitypolicyviolation', cspHandler);
    };
  }

  /**
   * Clear all logs and reset counts
   */
  clear(): void {
    this.logs = [];
    this.errorCount = 0;
    this.warningCount = 0;
    this.infoCount = 0;
  }
}
